import { FC, useCallback, useMemo, useState, useEffect, useRef } from "react";
import { CornerLabel } from "@datassential/platform-ui-lib";
import cn from "classnames";

import "./AiSearchResults.styles.scss";
import SearchSource from "./SearchSource";
import { IconAiStars } from "../../../../icons";
import { iFile } from "../../../../core/models/File";
import { IconLike, IconLiked } from '../../../../icons';
import Scrollbar from "../../../../components/Scrollbar/Scrollbar";
import PdfReader from "../../../../components/PdfReader/PdfReader";
import { iPublication } from "../../../../core/models/Publication";
import { HttpStatusCode } from "../../../../core/apiSchema";
import SearchSourceSkeleton from "./SearchSourceSkeleton";
import FileTypesEnum from "../../../../core/enums/FileTypesEnum";

interface iSourceFile extends iFile {
	title: string;
	initialPage?: number;
}

interface iAiSearchResultsProps {
	sources: iPublication[];
	statusCode: number;
	htmlContent?: string;
	isSuccess: boolean;
	isLoading?: boolean;
}

const MAP_STATUS_CODE_TO_ERROR_TEXT = {
	[HttpStatusCode.NUMBER_400]: {
		title: 'Hmm, I\'m feeling a bit scrambled...',
		subtitle: 'Can you crack that inquiry for me in a different way?'
	},
	[HttpStatusCode.NUMBER_404]: {
		title: 'Hmm, looks like my knowledge on this topic is still in the oven...',
		subtitle: 'Ask me a different question so I can give you an answer that\'s fully-baked.'
	}
}

const AiSearchResults: FC<iAiSearchResultsProps> = ({
	sources,
	statusCode,
	htmlContent,
	isSuccess,
	isLoading = false
}) => {
	const [isReadPopupOpen, setIsReadPopupOpen] = useState(false);
	const [file, setFile] = useState<iSourceFile | null>(null);
	const [isLiked, setIsLiked] = useState<boolean | null>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	const handleRead = useCallback((pdfFile: iFile, title: string, initialPage?: number) => {
		setFile({ ...pdfFile, title, initialPage });
		setIsReadPopupOpen(true);
	}, []);

	useEffect(() => {
		if (contentRef.current && htmlContent && sources.length > 0 && isSuccess) {
			setTimeout(() => {
				const citeTags = contentRef.current?.querySelectorAll('cite') || [];

				Array.from(citeTags).forEach(citeTag => {
					const citationId = citeTag.getAttribute('data-citation-id');
					if (!citationId) return;

					const matchingSource = sources.find(source => source.id.toString() === citationId);
					if (!matchingSource) return;

					const pdfFile = matchingSource.files.find(file => file.type === FileTypesEnum.Pdf);
					if (!pdfFile) return;

					citeTag.classList.add('is-clickable');
				});
			}, 0);
		}
	}, [sources, htmlContent, isSuccess]);

	useEffect(() => {
		const handleCiteClick = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			const citeElement = target.closest('cite');

			if (citeElement && citeElement.hasAttribute('data-citation-id')) {
				e.preventDefault();

				const citationId = citeElement.getAttribute('data-citation-id');
				if (!citationId) return;

				const citeText = citeElement.textContent || '';
				const pageMatch = citeText.match(/Page (\d+)/i);
				const pageNumber = pageMatch ? parseInt(pageMatch[1]) : undefined;

				const matchingSource = sources.find(source => source.id.toString() === citationId);
				if (!matchingSource) return;

				const pdfFile = matchingSource.files.find(file => file.type === FileTypesEnum.Pdf);
				if (!pdfFile) return;

				handleRead(pdfFile, matchingSource.title, pageNumber);
			}
		};

		const contentElement = contentRef.current;
		if (contentElement && isSuccess) {
			contentElement.addEventListener('click', handleCiteClick);
		}

		return () => {
			if (contentElement) {
				contentElement.removeEventListener('click', handleCiteClick);
			}
		};
	}, [sources, handleRead, isSuccess]);

	const onCloseReadPopup = useCallback(() => {
		setFile(null);
		setIsReadPopupOpen(false);
	}, []);

	const renderContent = useMemo(() => {


		if (statusCode === HttpStatusCode.NUMBER_400 || statusCode === HttpStatusCode.NUMBER_404) {
			return (
				<div className="AiSearchResults__error-container">
					<div className="AiSearchResults__error-title">
						{MAP_STATUS_CODE_TO_ERROR_TEXT[statusCode].title}
					</div>
					<div className="AiSearchResults__error-subtitle">
						{MAP_STATUS_CODE_TO_ERROR_TEXT[statusCode].subtitle}
					</div>
				</div>
			);
		}


		return (
			<div className="AiSearchResults__content-container">
				<div className="AiSearchResults__column AiSearchResults__answer">
					<div className="AiSearchResults__answer-header">
						<div className="AiSearchResults__column-header">
							<IconAiStars className="AiSearchResults__column-header-icon" />
							AI Generated Answer
						</div>
						<div className="AiSearchResults__answer-header-disclaimer">
							<span>
								Datassential's AI scours all of Report Pro so you don't have to. Need info from elsewhere in SNAP? Check&nbsp;
								<a href={process.env.REACT_APP_SNAP} target="_blank" rel="noreferrer">
									here
								</a>
								.
							</span>
						</div>
					</div>
					{htmlContent && (
						<div className="AiSearchResults__answer-container">
							<Scrollbar>
								<div className="AiSearchResults__answer-container-inner">
									<div
										className="AiSearchResults__html-content"
										dangerouslySetInnerHTML={{ __html: htmlContent }}
										ref={contentRef}
									/>
								</div>
								{isSuccess && (
									<div className="AiSearchResults__answer-feedback">
										Was this answer helpful?
										<div
											className={cn("AiSearchResults__feedback-icon")}
											data-testid="buttonLikeAiAnswer"
											onClick={() => setIsLiked(prev => prev === true ? null : true)}
										>
											{isLiked === true ? <IconLiked className="Icon32" /> : <IconLike className="Icon32" />}
										</div>
										<div
											className={cn("AiSearchResults__feedback-icon is-dislike-icon")}
											data-testid="buttonDislikeAiAnswer"
											onClick={() => setIsLiked(prev => prev === false ? null : false)}
										>
											{isLiked === false ? <IconLiked className="Icon32" /> : <IconLike className="Icon32" />}
										</div>
									</div>
								)}
							</Scrollbar>
						</div>
					)}
				</div>

				<div className="AiSearchResults__vertical-divider" />
				<div className="AiSearchResults__column AiSearchResults__sources">
					<div className="AiSearchResults__column-header">Sources</div>
					<div className="AiSearchResults__sources-list">
						<Scrollbar>

							<div className="AiSearchResults__sources-list-inner" style={{ position: 'relative' }}>
								{isLoading
									? <SearchSourceSkeleton />
									: sources.map((source) => (
										<SearchSource
											key={source.id}
											source={source}
											onRead={handleRead}
										/>
									))
								}

							</div>
						</Scrollbar>
					</div>
				</div>
			</div>
		);
	}, [handleRead, isLiked, sources, htmlContent, statusCode, isSuccess, isLoading]);

	return (
		<div className="AiSearchResults">
			<CornerLabel modifiers={["top", "right"]}>BETA</CornerLabel>
			{renderContent}

			{!!file && !!file.name && (
				<PdfReader
					isOpened={isReadPopupOpen}
					close={onCloseReadPopup}
					title={file.title}
					fileName={file.name}
					initialPage={file.initialPage}
				/>
			)}
		</div>
	);
};

export default AiSearchResults;
